/**
 * Theme manager — light / dark / system.
 *
 * Applies `.dark` class on <html> based on stored preference or system setting.
 * Persists the user choice to localStorage under `accucore-theme`.
 *
 * Call `initTheme()` before React mounts to avoid a flash of wrong theme.
 */

export type Theme = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'accucore-theme'

/** Get the stored theme choice. Defaults to 'system'. */
export function getTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'system'
  const stored = localStorage.getItem(STORAGE_KEY) as Theme | null
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  return 'system'
}

/** Persist the theme choice and apply it to <html>. */
export function setTheme(theme: Theme): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, theme)
  }
  applyTheme(resolveTheme(theme))
}

/** Resolve 'system' to 'light' | 'dark' using prefers-color-scheme. */
export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'light' || theme === 'dark') return theme
  if (typeof matchMedia === 'undefined') return 'light'
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** Apply a resolved theme to the <html> element. */
export function applyTheme(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (theme === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

/**
 * Initialise theme before React mounts. Call once from main.tsx.
 * Also sets up a listener that reacts to system preference changes when
 * the user's choice is 'system'.
 */
export function initTheme(): void {
  const stored = getTheme()
  applyTheme(resolveTheme(stored))

  if (typeof matchMedia !== 'undefined') {
    const mq = matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', () => {
      if (getTheme() === 'system') {
        applyTheme(mq.matches ? 'dark' : 'light')
      }
    })
  }
}
